import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAppBranding } from '../../../config/branding';
import { AccountLevelInterface } from '../../../interface/account-level.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomInput from '../../../shared-components/custom-input/CustomInput';
import SelectFiled from '../../../shared-components/select-field/SelectField';
import accountApi from '../../../utils/api/account';
import feeConfiguration from '../../../utils/api/fee-configuration';

interface SidePanelAddFeeProperties {
  onClose: () => void;
  onRefresh: () => void;
  data?: AccountLevelInterface | null;
  mode?: 'add' | 'view' | 'edit';
}

interface FeeFormInterface {
  accountCustomId: string;
  accountName: string;
  platformFee: string;
  platformFeeType: string;
  sessionFee: string;
  sessionFeeType: string;
}
interface FeePayload {
  accountCustomId: string;
  sessionFee?: number;
  sessionFeeType?: 'amount' | 'percentage';
  platformFee?: number;
  platformFeeType?: 'amount' | 'percentage';
}
/**
 * Side Panel Add Fee Component
 * @param properties - The component properties
 * @returns JSX Element for the add fee side panel
 */
function SidePanelAddFee(properties: SidePanelAddFeeProperties) {
  const branding = useAppBranding();
  const { onClose, onRefresh, data, mode } = properties;
  const { t, i18n } = useTranslation('sharedComponent');
  const dispatch = useAppDispatch();
  const { settings } = useAppSelector((state) => state.accountLevel);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const platformFeeType = settings?.platformFeeType === 'amount' ? 'currency' : 'percentage';
  const sessionFeeType = settings?.sessionFeeType === 'amount' ? 'currency' : 'percentage';
  const [formData, setFormData] = useState<FeeFormInterface>({
    accountCustomId: '',
    accountName: '',
    platformFee: mode === 'add' ? String(settings?.platformFee ?? '') : '',
    platformFeeType: mode === 'add' ? platformFeeType || 'percentage' : 'percentage',
    sessionFee: mode === 'add' ? String(settings?.sessionFee ?? '') : '',
    sessionFeeType: mode === 'add' ? sessionFeeType || 'percentage' : 'percentage',
  });

  useEffect(() => {
    if (mode === 'edit' && data) {
      /**
       * Get fee details from settings or data
       * @param field - 'platformFee' or 'sessionFee'
       * @returns The fee value and type
       */
      const getFeeDetails = (field: 'platformFee' | 'sessionFee') => {
        let value = '';
        let type = 'percentage';

        if (data.settings && data.settings[field] !== undefined) {
          value = String(data.settings[field]);
          type = data.settings[`${field}Type`] === 'amount' ? 'currency' : 'percentage';
        } else if (data[field] !== undefined) {
          value = String(data[field]);
          type = (data as any)[`${field}Type`] === 'amount' ? 'currency' : 'percentage';
        }
        return { value, type };
      };

      const platformFeeDetails = getFeeDetails('platformFee');
      const sessionFeeDetails = getFeeDetails('sessionFee');

      setFormData({
        accountCustomId: data.accountCustomId || '',
        accountName: data.accountName || '',
        platformFee: platformFeeDetails.value,
        platformFeeType: platformFeeDetails.type,
        sessionFee: sessionFeeDetails.value,
        sessionFeeType: sessionFeeDetails.type,
      });
    }
  }, [mode, data]);

  /**
   * Handle Input Change
   * @param name - The name of the input field
   * @param value - The changed value
   */
  const handleInputChange = (name: string, value: any) => {
    setErrorMessage(false);

    let newValue = value; // ✅ create new variable

    // Platform Fee Validation
    if (name === 'platformFee' && formData.platformFeeType === 'percentage') {
      if (Number(newValue) > 100) newValue = 100;
      if (Number(newValue) < 0) newValue = 0;
    }

    // Session Fee Validation
    if (name === 'sessionFee' && formData.sessionFeeType === 'percentage') {
      if (Number(newValue) > 100) newValue = 100;
      if (Number(newValue) < 0) newValue = 0;
    }

    setFormData((previous) => ({ ...previous, [name]: newValue }));
  };
  /**
   * Fetch Account Name
   * @returns A promise that resolves when the account is fetched
   */
  const fetchAccountName = async () => {
    try {
      if (!formData.accountCustomId || mode === 'edit') return;
      const response = await accountApi.fetchAccounts({ searchBy: formData.accountCustomId });
      if (response?.data?.results?.length) {
        setFormData((previous) => ({
          ...previous,
          accountName: response.data.results[0].accountName || 'N/A',
        }));
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (mode !== 'edit') {
      fetchAccountName();
    }
  }, [formData.accountCustomId]);

  /**
   * Handle Submit
   * @returns A promise that resolves on submit
   */
  const handleSubmit = async () => {
    setButtonLoading(true);
    if (
      formData?.accountCustomId &&
      (formData?.platformFee !== '' || formData?.sessionFee !== '')
    ) {
      try {
        const payload: FeePayload = {
          accountCustomId: formData?.accountCustomId,
        };
        if (
          formData.sessionFee !== '' &&
          formData.sessionFee !== null &&
          formData.sessionFee !== undefined
        ) {
          payload.sessionFee = Number(formData?.sessionFee);
          payload.sessionFeeType =
            formData?.sessionFeeType === 'currency' ? 'amount' : 'percentage';
        }
        if (
          formData.platformFee !== '' &&
          formData.platformFee !== null &&
          formData.platformFee !== undefined
        ) {
          payload.platformFee = Number(formData?.platformFee);
          payload.platformFeeType =
            formData?.platformFeeType === 'currency' ? 'amount' : 'percentage';
        }

        if (formData.platformFeeType === 'percentage' && Number(formData.platformFee) > 100) {
          dispatch(
            setNotificationMessage({
              message: 'Percentage cannot exceed 100%',
              status: true,
              type: 'error',
            }),
          );
          setButtonLoading(false);
          return;
        }

        if (formData.sessionFeeType === 'percentage' && Number(formData.sessionFee) > 100) {
          dispatch(
            setNotificationMessage({
              message: 'Percentage cannot exceed 100%',
              status: true,
              type: 'error',
            }),
          );
          setButtonLoading(false);
          return;
        }
        await feeConfiguration.createAccountLevel(payload);

        onRefresh();
        onClose();
      } catch (error: any) {
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message || t('somethingWentWrong'),
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      } finally {
        setButtonLoading(false);
      }
    } else {
      setButtonLoading(false);
      setErrorMessage(true);
      dispatch(
        setNotificationMessage({
          message: !formData.accountCustomId ? t('enterAccountId') : t('pleaseEnterFee'),
          status: true,
          type: 'error',
        }),
      );
    }
  };

  return (
    <div className="account-fee" dir={i18n.dir()}>
      <div className="form-header">
        <h3>{mode === 'edit' ? t('editOverride') : t('addOverride')}</h3>
      </div>
      <div
        className="form-body"
        style={{ overflowY: 'scroll', height: window.innerHeight >= 700 ? 300 : 352 }}>
        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="accountCustomId"
              inputLabel={t('accountId')}
              placeholder={t('enterAccountId')}
              value={formData.accountCustomId}
              type="text"
              onChangeValue={handleInputChange}
              error={errorMessage && !formData.accountCustomId}
              errorMessage={t('required')}
              valueRequired
              disabled={mode === 'edit'}
            />
          </div>
          <div className="form-field">
            <CustomInput
              name="accountName"
              inputLabel={t('accountName')}
              placeholder=""
              type="text"
              value={formData.accountName}
              onChangeValue={() => {}}
              disabled
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="platformFee"
              inputLabel={t('platformFee')}
              placeholder={t('enterValue')}
              type="number"
              value={formData.platformFee}
              onChangeValue={handleInputChange}
            />
          </div>
          <div className="form-field">
            <SelectFiled
              name="platformFeeType"
              inputLabel={t('type')}
              selectedValue={formData.platformFeeType}
              options={[
                { label: '%', value: 'percentage' },
                { label: branding.text.currency, value: 'currency' },
              ]}
              onChangeValue={handleInputChange}
              placeholder={t('select')}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-field">
            <CustomInput
              name="sessionFee"
              inputLabel={t('sessionFee')}
              placeholder={t('enterValue')}
              type="number"
              value={formData.sessionFee}
              onChangeValue={handleInputChange}
            />
          </div>
          <div className="form-field">
            <SelectFiled
              name="sessionFeeType"
              inputLabel={t('type')}
              selectedValue={formData.sessionFeeType}
              options={[
                { label: '%', value: 'percentage' },
                { label: 'Currency', value: 'currency' },
              ]}
              onChangeValue={handleInputChange}
              placeholder={t('select')}
            />
          </div>
        </div>
      </div>

      <div className="form-bottom-button">
        <div className="table-button-item">
          <MyButton label={t('cancel')} buttonType="outline" onClickFunc={onClose} />
        </div>
        <div className="table-button-item">
          <MyButton
            label={t('submit')}
            buttonType="submit"
            onClickFunc={handleSubmit}
            loading={buttonLoading}
          />
        </div>
      </div>
    </div>
  );
}

export default SidePanelAddFee;
